import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Plus, Edit, Trash2, Package, AlertTriangle, CheckCircle2 } from 'lucide-react'

export function EquipmentManager() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState<any>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['equipment'],
    queryFn: async () => {
      const res = await fetch('/api/manage/equipment')
      return res.json() as Promise<{ equipment: any[] }>
    },
  })

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch('/api/manage/equipment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to create equipment')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] })
      setIsDialogOpen(false)
      setEditingEquipment(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await fetch(`/api/manage/equipment/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to update equipment')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] })
      setIsDialogOpen(false)
      setEditingEquipment(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/manage/equipment/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete equipment')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      equipment_type: formData.get('equipment_type'),
      manufacturer: formData.get('manufacturer'),
      model: formData.get('model'),
      serial_number: formData.get('serial_number'),
      imei: formData.get('imei'),
      firmware_version: formData.get('firmware_version'),
      status: formData.get('status'),
      assigned_vehicle_id: formData.get('assigned_vehicle_id') || null,
      assigned_driver_id: formData.get('assigned_driver_id') || null,
      purchase_date: formData.get('purchase_date') ? new Date(formData.get('purchase_date') as string).getTime() / 1000 : null,
      warranty_expires: formData.get('warranty_expires') ? new Date(formData.get('warranty_expires') as string).getTime() / 1000 : null,
      notes: formData.get('notes'),
    }

    if (editingEquipment) {
      updateMutation.mutate({ id: editingEquipment.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (equipment: any) => {
    setEditingEquipment(equipment)
    setIsDialogOpen(true)
  }

  const handleDelete = (id: string, serial: string) => {
    if (confirm(`Delete equipment "${serial}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id)
    }
  }

  const isWarrantyExpiring = (expiresTimestamp: number) => {
    if (!expiresTimestamp) return false
    const expiresDate = new Date(expiresTimestamp * 1000)
    const now = new Date()
    const daysUntilExpiry = Math.floor((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0
  }

  const isWarrantyExpired = (expiresTimestamp: number) => {
    if (!expiresTimestamp) return false
    const expiresDate = new Date(expiresTimestamp * 1000)
    return expiresDate < new Date()
  }

  const formatDate = (timestamp: number) => {
    if (!timestamp) return '-'
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  const formatEquipmentType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'maintenance':
        return 'destructive'
      case 'inactive':
        return 'secondary'
      case 'retired':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Equipment & Device Tracking
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingEquipment(null)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Equipment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingEquipment ? 'Edit Equipment' : 'Add New Equipment'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Equipment Type *</Label>
                    <Select name="equipment_type" defaultValue={editingEquipment?.equipment_type || 'gps_tracker'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gps_tracker">GPS Tracker</SelectItem>
                        <SelectItem value="eld_unit">ELD Unit</SelectItem>
                        <SelectItem value="dash_cam">Dash Camera</SelectItem>
                        <SelectItem value="sensor">Sensor</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingEquipment?.status || 'active'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="retired">Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Manufacturer</Label>
                    <Input name="manufacturer" defaultValue={editingEquipment?.manufacturer} />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input name="model" defaultValue={editingEquipment?.model} />
                  </div>
                  <div>
                    <Label>Serial Number *</Label>
                    <Input name="serial_number" defaultValue={editingEquipment?.serial_number} required />
                  </div>
                  <div>
                    <Label>IMEI (for cellular devices)</Label>
                    <Input name="imei" defaultValue={editingEquipment?.imei} />
                  </div>
                  <div>
                    <Label>Firmware Version</Label>
                    <Input name="firmware_version" defaultValue={editingEquipment?.firmware_version} />
                  </div>
                  <div>
                    <Label>Purchase Date</Label>
                    <Input
                      name="purchase_date"
                      type="date"
                      defaultValue={editingEquipment?.purchase_date ? new Date(editingEquipment.purchase_date * 1000).toISOString().split('T')[0] : ''}
                    />
                  </div>
                  <div>
                    <Label>Warranty Expires</Label>
                    <Input
                      name="warranty_expires"
                      type="date"
                      defaultValue={editingEquipment?.warranty_expires ? new Date(editingEquipment.warranty_expires * 1000).toISOString().split('T')[0] : ''}
                    />
                  </div>
                  <div>
                    <Label>Assigned Vehicle (Optional)</Label>
                    <Input name="assigned_vehicle_id" placeholder="veh_001" defaultValue={editingEquipment?.assigned_vehicle_id} />
                  </div>
                  <div>
                    <Label>Assigned Driver (Optional)</Label>
                    <Input name="assigned_driver_id" placeholder="drv_001" defaultValue={editingEquipment?.assigned_driver_id} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea name="notes" defaultValue={editingEquipment?.notes} rows={3} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingEquipment ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Manufacturer/Model</TableHead>
                <TableHead>Serial Number</TableHead>
                <TableHead>Assignments</TableHead>
                <TableHead>Warranty</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.equipment?.map((equipment) => (
                <TableRow key={equipment.id}>
                  <TableCell className="font-medium">{formatEquipmentType(equipment.equipment_type)}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {equipment.manufacturer && <div>{equipment.manufacturer}</div>}
                      {equipment.model && <div className="text-muted-foreground">{equipment.model}</div>}
                      {equipment.firmware_version && (
                        <div className="text-xs text-muted-foreground">v{equipment.firmware_version}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs font-mono">{equipment.serial_number}</TableCell>
                  <TableCell>
                    <div className="text-xs">
                      {equipment.assigned_vehicle_id && (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Vehicle:</span>
                          <span className="font-mono">{equipment.assigned_vehicle_id}</span>
                        </div>
                      )}
                      {equipment.assigned_driver_id && (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Driver:</span>
                          <span className="font-mono">{equipment.assigned_driver_id}</span>
                        </div>
                      )}
                      {!equipment.assigned_vehicle_id && !equipment.assigned_driver_id && (
                        <span className="text-muted-foreground">Unassigned</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {equipment.warranty_expires ? (
                      <div className="flex items-center gap-1">
                        {isWarrantyExpired(equipment.warranty_expires) ? (
                          <Badge variant="outline" className="text-xs border-red-500 text-red-600">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Expired
                          </Badge>
                        ) : isWarrantyExpiring(equipment.warranty_expires) ? (
                          <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-600">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {formatDate(equipment.warranty_expires)}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {formatDate(equipment.warranty_expires)}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusColor(equipment.status)} className="capitalize">
                      {equipment.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(equipment)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(equipment.id, equipment.serial_number)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
